import { ConnectError } from '@bufbuild/connect';
import { Timestamp } from '@bufbuild/protobuf';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { OrganizationAPIKey } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { CalledVia } from '@/shared/api/protocol-ts/model/dto_base_pb';
import { SliceName } from '@/shared/config';
import { useAppDispatch, useCopyToClipboard } from '@/shared/hooks';
import {
  Button,
  errorToastCaller,
  Input,
  toastCaller,
  WidgetCard,
} from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import { TableWithInfiniteScroll } from '@/widgets/TableWithInfiniteScroll';

import {
  API_KEY_COMMENT_MAX_LENGTH,
  ApiKeyFormPayload,
  apiKeyValidationSchema,
  EMPTY_VALUE,
  i18n,
} from '../../config';
import { useGetApiKeysColumns, useResolveApiKeyAuthor } from '../../hooks';
import {
  formatApiKeyTimestamp,
  formatDateToLocalInput,
  getApiKeyDefaultExpiresAt,
} from '../../lib';

import styles from './OrganizationApiKeys.module.scss';

type OrganizationApiKeysProps = {
  organizationID: string;
  canCreate: boolean;
  canDelete: boolean;
  className?: string;
};

export const OrganizationApiKeys: FC<OrganizationApiKeysProps> = (props) => {
  const { organizationID, canCreate, canDelete, className } = props;

  const { formatMessage, formatDate } = useIntl();
  const dispatch = useAppDispatch();
  const resolveAuthor = useResolveApiKeyAuthor();
  const copyToClipboard = useCopyToClipboard();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ApiKeyFormPayload>({
    resolver: yupResolver(apiKeyValidationSchema),
    mode: 'all',
    defaultValues: { expiresAt: getApiKeyDefaultExpiresAt(), comment: '' },
  });

  const [apiKeys, setApiKeys] = useState<OrganizationAPIKey[]>([]);
  const [createdKey, setCreatedKey] = useState<OrganizationAPIKey | null>(null);

  const fetchKeys = async () => {
    try {
      const { APIKeys } = await dispatch(
        authModel.thunks.listOrganizationApiKeys({
          OrganizationID: organizationID,
        }),
      ).unwrap();

      setApiKeys(APIKeys);
    } catch (error) {
      errorToastCaller(error as ConnectError);
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationID]);

  const handleCreate: SubmitHandler<ApiKeyFormPayload> = async ({
    expiresAt,
    comment,
  }) => {
    try {
      const { APIKey } = await dispatch(
        authModel.thunks.createOrganizationApiKey({
          OrganizationID: organizationID,
          ExpiresAt: Timestamp.fromDate(new Date(expiresAt)),
          Comment: comment.trim(),
        }),
      ).unwrap();

      setCreatedKey(APIKey ?? null);
      reset({ expiresAt: getApiKeyDefaultExpiresAt(), comment: '' });
      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'companySettings.apiKeys.createSuccess',
          defaultMessage: 'API key created',
        }),
      });

      await fetchKeys();
    } catch (error) {
      errorToastCaller(error as ConnectError);
    }
  };

  const handleDelete = (apiKey: OrganizationAPIKey) => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage({
            id: 'companySettings.apiKeys.deleteConfirmTitle',
            defaultMessage: 'Delete this API key?',
          }),
          description: formatMessage({
            id: 'companySettings.apiKeys.deleteConfirmDescription',
            defaultMessage:
              'Any integration using this key will immediately stop working. This cannot be undone.',
          }),
          okText: formatMessage({
            id: 'global.delete',
            defaultMessage: 'Delete',
          }),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));

            try {
              await dispatch(
                authModel.thunks.deleteOrganizationApiKey({
                  OrganizationID: organizationID,
                  ID: apiKey.ID,
                }),
              ).unwrap();

              setCreatedKey((current) =>
                current?.ID === apiKey.ID ? null : current,
              );
              toastCaller({
                type: 'success',
                message: formatMessage({
                  id: 'companySettings.apiKeys.deleteSuccess',
                  defaultMessage: 'API key deleted',
                }),
              });

              await fetchKeys();
            } catch (error) {
              errorToastCaller(error as ConnectError);
            }
          },
        },
      }),
    );
  };

  const renderCreatedVia = (via?: CalledVia) => {
    const viaMessage =
      via !== undefined ? i18n.apiKeyCalledViaLabels[via] : undefined;

    return viaMessage ? formatMessage(viaMessage) : EMPTY_VALUE;
  };

  const columnsScheme = useGetApiKeysColumns({
    canDelete,
    onDelete: handleDelete,
  });

  return (
    <div
      className={cn(styles.container, className)}
      data-component="organization-api-keys"
    >
      {canCreate && (
        <WidgetCard className={styles.card}>
          <div>
            <h4 className="h4">
              {formatMessage({
                id: 'companySettings.apiKeys.title',
                defaultMessage: 'Create API key',
              })}
            </h4>
            <p className={cn(styles.subText, 'p2')}>
              {formatMessage({
                id: 'companySettings.apiKeys.description',
                defaultMessage:
                  'Generate an API key for this organization. The secret is shown only once, right after creation — copy and store it securely.',
              })}
            </p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit(handleCreate)}>
            <Controller
              control={control}
              name="expiresAt"
              render={({
                field: { ref, value, name, onBlur, onChange },
                fieldState: { error },
              }) => (
                <Input
                  ref={ref}
                  name={name}
                  type="datetime-local"
                  required
                  label={formatMessage({
                    id: 'companySettings.apiKeys.expiresAt.label',
                    defaultMessage: 'Expires at',
                  })}
                  value={value}
                  min={formatDateToLocalInput(new Date())}
                  error={error?.message}
                  onBlur={onBlur}
                  onChange={onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="comment"
              render={({
                field: { ref, value, name, onBlur, onChange },
                fieldState: { error },
              }) => (
                <Input
                  ref={ref}
                  name={name}
                  label={formatMessage({
                    id: 'companySettings.apiKeys.comment.label',
                    defaultMessage: 'Comment',
                  })}
                  placeholder={formatMessage({
                    id: 'companySettings.apiKeys.comment.placeholder',
                    defaultMessage: 'Optional note, e.g. what this key is for',
                  })}
                  value={value}
                  maxLength={API_KEY_COMMENT_MAX_LENGTH}
                  error={error?.message}
                  onBlur={onBlur}
                  onChange={onChange}
                />
              )}
            />

            <div>
              <Button variant="primary" type="submit" loading={isSubmitting}>
                {formatMessage({
                  id: 'companySettings.apiKeys.submit',
                  defaultMessage: 'Create API key',
                })}
              </Button>
            </div>
          </form>
        </WidgetCard>
      )}

      {createdKey && (
        <WidgetCard className={styles.card}>
          <h5 className="h5">
            {formatMessage({
              id: 'companySettings.apiKeys.resultTitle',
              defaultMessage: 'API key created — copy the secret now',
            })}
          </h5>
          <p className={cn(styles.warning, 'p3')}>
            {formatMessage({
              id: 'companySettings.apiKeys.resultWarning',
              defaultMessage:
                'This is the only time the secret will be shown. Once you leave this page it cannot be retrieved again.',
            })}
          </p>

          <div className={styles.secretBlock}>
            <p className={cn(styles.subText, 'p3')}>
              {formatMessage({
                id: 'companySettings.apiKeys.secret.label',
                defaultMessage: 'API key secret',
              })}
            </p>
            <div className={styles.secretRow}>
              <Input
                name="apiKeySecret"
                value={createdKey.Secret}
                readOnly
                inputClassName={styles.secretValue}
                onChange={() => undefined}
              />
              <Button
                variant="secondary"
                icon="copy"
                onClick={() =>
                  copyToClipboard(
                    createdKey.Secret,
                    formatMessage({
                      id: 'companySettings.apiKeys.copySuccess',
                      defaultMessage: 'API key secret copied to clipboard',
                    }),
                  )
                }
              >
                {formatMessage({ id: 'global.copy', defaultMessage: 'Copy' })}
              </Button>
            </div>
          </div>

          <dl className={styles.meta}>
            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.col.id',
                defaultMessage: 'Key ID',
              })}
            </dt>
            <dd className={styles.keyId}>{createdKey.ID}</dd>

            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.col.createdBy',
                defaultMessage: 'Created by',
              })}
            </dt>
            <dd>{resolveAuthor(createdKey.Created?.By)}</dd>

            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.col.createdAt',
                defaultMessage: 'Created at',
              })}
            </dt>
            <dd>{formatApiKeyTimestamp(formatDate, createdKey.Created?.At)}</dd>

            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.col.comment',
                defaultMessage: 'Comment',
              })}
            </dt>
            <dd>{createdKey.Created?.Comment || EMPTY_VALUE}</dd>

            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.via',
                defaultMessage: 'Created via',
              })}
            </dt>
            <dd>{renderCreatedVia(createdKey.Created?.Via)}</dd>

            <dt className={styles.subText}>
              {formatMessage({
                id: 'companySettings.apiKeys.col.expiresAt',
                defaultMessage: 'Expires at',
              })}
            </dt>
            <dd>{formatApiKeyTimestamp(formatDate, createdKey.ExpiresAt)}</dd>
          </dl>

          <div>
            <Button variant="tertiary" onClick={() => setCreatedKey(null)}>
              {formatMessage({
                id: 'companySettings.apiKeys.createAnother',
                defaultMessage: 'Create another key',
              })}
            </Button>
          </div>
        </WidgetCard>
      )}

      <div className={styles.listSection}>
        <h5 className="h5">
          {formatMessage({
            id: 'companySettings.apiKeys.listTitle',
            defaultMessage: 'Existing API keys',
          })}
        </h5>

        {apiKeys.length === 0 ? (
          <p className="p2">
            {formatMessage({
              id: 'companySettings.apiKeys.empty',
              defaultMessage: 'No API keys yet',
            })}
          </p>
        ) : (
          <WidgetCard className={styles.card}>
            {/* Static list: reFetchList is a no-op; loadingState only names the auth domain. */}
            <TableWithInfiniteScroll
              listData={apiKeys}
              reFetchList={() => undefined}
              itemCounter={apiKeys.length}
              columnsScheme={columnsScheme}
              loadingState={SliceName.auth}
            />
          </WidgetCard>
        )}
      </div>
    </div>
  );
};
